import { TreeOfNodes } from "@asup/tree-of-nodes";
import { Control210 } from "aic";
import { useHierarchyContext, useTocContext } from "contexts/AppSettingsContext";
import { getHierarchyBaseUrl } from "contexts/hierarchy/getHierarchyBaseUrl";
import { getTreeListFromTocItems } from "contexts/toc/getTreeListFromTocItems";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { Key, ReactNode, useState } from "react";
import { Container } from "react-bootstrap";
import { useLocation, useParams } from "react-router-dom";
import "./TocTree.css";

/**
 * Tree view of the Table of Contents with filtering and selection navigation.
 * Selecting a node navigates to the corresponding ToC item review URL.
 *
 * @returns A container with the ToC tree and filter control.
 */
export const TocTree = (): ReactNode => {
  const location = useLocation();
  const params = useParams();
  const hierarchyContext = useHierarchyContext();
  const hierarchy = hierarchyContext?.hierarchy;

  const { navigate } = useUnsavedChanges("toc-tree");
  const { state } = useTocContext();

  const [filter, setFilter] = useState<string>("");

  const tocList = getTreeListFromTocItems(state.tocItems, filter);
  const selectedId = params.tocNumber
    ? tocList.find((n) => n.data === params.tocNumber)?.id
    : undefined;

  /**
   * Handles selection of a ToC tree node by navigating to its review URL.
   *
   * @param id - The identifier of the selected tree node.
   */
  const handleSelect = (id: Key | Key[]) => {
    /* istanbul ignore else */
    if (hierarchy) {
      const node = tocList.find((n) => n.id === id);
      /* istanbul ignore else */
      if (node?.data) {
        const newUrl = `${getHierarchyBaseUrl(hierarchy)}/Review/${node.data}`;
        /* istanbul ignore else */
        if (newUrl !== location.pathname) navigate(newUrl);
      }
    }
  };

  return (
    <Container
      id="toc-tree-container"
      className="w-100 ml-2 mt-2"
    >
      <h4 className="text-lg">Table of contents</h4>
      <Control210
        value={filter}
        setValue={setFilter}
        label="Filter"
        id="toc-tree-filter"
        labelWidth="auto"
      />
      <TreeOfNodes
        id="toc-tree"
        nodeList={tocList}
        roots={tocList.filter((n) => !n.parentId).map((n) => n.id)}
        handleSelect={handleSelect}
        selected={selectedId ? [selectedId] : []}
        nodeHighlight="rgb(33,35,241)"
        textHighlight="rgba(33,35,211,0.1)"
      />
    </Container>
  );
};

TocTree.displayName = "TocTree";
